import { useState } from 'react'
import { useEffect } from 'react'
import { memo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { REACTOR_STYLE } from './Reactor'
import { ZINDEX_CONTROLS, ZINDEX_FULLSCREEN_VIDEO } from './SwipeCard'
import { toggleControls } from 'redux/actions/playerControlActions'
import { getMediaDisplay } from 'util/imageUtils'
import { downloadImageFile } from 'controllers/BackblazeController'
import { getVideoSrc } from 'util/playlistUtils'
import { extractColors } from 'extract-colors'
import { getMediaThumbnail } from 'util/imageUtils'
import { log } from 'components/utility/DisplayLog'
import { useRef } from 'react'
import { forwardRef } from 'react'
import { PLAYER_MODES } from 'redux/reducers/harmonizeReducer'
import { cropImageToCover } from 'util/imageUtils'


const MediaHtmlComponent = forwardRef(function (props, controlRef) {
    const { videoSource, muted, videoStyle, controls, loop, click, autoHeight, videoOnly } = props

    const { containDisplay, reactorPlay } = useSelector(state => state.display)
    const { playListIndex, playerMode, showAi } = useSelector(state => state.harmonize)
    const { playList } = useSelector(state => state.messages)
    const { mediaSelected } = useSelector(state => state.chat)
    const [portrait, setIsPortrait] = useState(window.innerWidth > window.innerHeight ? false : true)
    const [imageSrc, setImageSrc] = useState()
    const [dominantColor, setDominantColor] = useState()
    const dispatch = useDispatch()
    const nextImageIndex = useRef()
    const nextImageRef = useRef()
    const nextImageSrcRef = useRef()
    const [containerHeight, setContainerHeight] = useState('100vh')
    const containerRef = useRef(null)

    const getHeight = () => {
        return mediaSelected ? '60vh' : autoHeight || showAi ? 'auto' : reactorPlay ? REACTOR_STYLE.top : containerHeight
    }

    const getSong = () => {
        return playList[playListIndex]
    }

    /** In cases where the specified videoStyle is "contain" (e.g. AIChat), contain regardless
     * of containDisplay setting.
     */
    const shouldContainDisplay = () => {
        return (videoStyle && videoStyle.objectFit && videoStyle.objectFit === 'contain') || containDisplay
    }
    /**
     * If the current playList entry is video, see if the next one is audio, and if so,
     * retrieve it for use when the next entry is displayed.
     * @returns {nextSong,index} or null
     */
    const getNextSong = () => {
        if (getVideoSrc(getSong()) && playListIndex + 1 < playList.length) {
            const nextSong = playList[playListIndex + 1]
            if (nextSong) {
                const imageMedia = getMediaDisplay(nextSong.entryMedia)
                if (imageMedia) {
                    log(`----------got audio song at ${playListIndex + 1}`)
                    return { nextSong, index: playListIndex + 1 }
                }
            }

        }
        return null
    }

    const getVideoContainerStyle = () => {
        //log(`<>MediaHtmlComponent getVideoContainerStyle videoOnly ${videoOnly} shouldContainDisplay() ${shouldContainDisplay()} imageSrc`, imageSrc)
        if (!videoOnly && imageSrc) {
            return {
                position: 'relative',
                color: 'white',
                aspectRatio: '9/16',
                height: getHeight(),
                width: '100vw',
                overflowY: 'hidden',
                backgroundImage: 'url(' + imageSrc + ')',
                backgroundSize: shouldContainDisplay() ? 'contain' : 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundColor: dominantColor,
                opacity: 1
            }
        }
    }
    /**
     * As of 2.28 a video can have an image which is used as the "poster". The simplest way to do this
     * is to use it as the backgroundImage for the video style if the video has a posterImage.
     * @returns 
     */
    const getVideoStyle = () => {
        if (videoStyle) {
            return videoStyle
        } else if (getVideoSrc(getSong())) {
            //console.log(`MediaHtmlComponent getVideoStyle shouldContainDisplay() ${shouldContainDisplay()} portrait ${portrait} imageSrc ${imageSrc}`)
            if (!videoOnly && imageSrc) {
                return {
                    width: portrait ? '100%' : '100vw',
                    objectFit: shouldContainDisplay() ? 'contain' : 'cover',
                    zIndex: shouldContainDisplay() ? ZINDEX_FULLSCREEN_VIDEO : ZINDEX_CONTROLS - 1,
                    height: getHeight(),
                    overflowY: 'hidden'
                }
            } else {
                return {
                    width: portrait ? '100%' : '100vw',
                    objectFit: shouldContainDisplay() ? 'contain' : 'cover',
                    overflowY: 'hidden',
                    zIndex: shouldContainDisplay() ? ZINDEX_FULLSCREEN_VIDEO : ZINDEX_CONTROLS - 1,
                    backgroundColor: playerMode === PLAYER_MODES.VIDEO_ONLY ? 'transparent' : 'black',
                    height: getHeight()
                }
            }
        } else {
            return {
                position: 'relative',
                color: 'white',
                aspectRatio: '9/16',
                height: getHeight(),
                width: '100vw',
                overflowY: 'hidden',
                backgroundImage: 'url(' + imageSrc + ')',
                backgroundSize: shouldContainDisplay() ? 'contain' : 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundColor: dominantColor,
                opacity: 1
            }
        }

    }
    const getDominantColor = (colors) => {
        const maxArea = Math.max(...colors.map(c => c.area))
        const color = colors.find(c => c.area === maxArea)
        setDominantColor(color.hex)
    }

    const getBackgroundColor = async (colorSrc) => {
        try {
            const result = await extractColors(colorSrc)
            getDominantColor(result)
        } catch (error) {
            //displayError(error, setError, 50)
        }
    }

    const cropOrContainImage = async (src) => {
        await getBackgroundColor(src)

        cropImageToCover(src, window.innerWidth, window.innerHeight,
            parseFloat(process.env.REACT_APP_UPLOAD_IMAGE_QUALITY),
            (croppedData) => {
                const src = URL.createObjectURL(croppedData)
                setImageSrc(src)
            })

    }
    /**
     * Get the current Message and if it has imageMedai, check to 
     * see if we already downloaded it in nextSongImage. If so set
     * that as the img src; otherwise get the media from the Message
     * we just read.
     */
    const getImageMedia = async () => {
        if (getSong()) {
            const { entryMedia: media, messageId } = getSong()
            //log(`DOWNLOAD: getImageMedia ${messageId}`, media)
            const startTime = Date.now()
            if (media) {
                try {
                    let imageMedia = getMediaDisplay(media)
                    if (!imageMedia) {
                        imageMedia = getMediaThumbnail(media)
                    }
                    if (imageMedia) {
                        const { source } = imageMedia
                        if (source === nextImageRef.current && playListIndex === nextImageIndex.current) {
                            console.log(`display from preloaded song ${nextImageRef.current}`)

                            setImageSrc(nextImageSrcRef.current)
                            nextImageRef.current = null
                            await getBackgroundColor(nextImageSrcRef.current)


                        } else {
                            const data = await downloadImageFile(source)
                            //console.log(`DOWNLOAD: complete time ${Date.now() - startTime}`)
                            const src = URL.createObjectURL(data)

                            setImageSrc(src)
                            nextImageSrcRef.current = null
                            await getBackgroundColor(src)


                        }
                    } else {
                        setImageSrc(null)
                    }
                } catch (error) {
                    //displayError(error, setError, 51)
                }
            }
            await nextSongImage()
        }

    }

    /**
     * Look ahead for the next song (i.e. Message with an image) and if
     * found, download that into nextImageSrcRef and sets nextImageRef to the
     * download src.
     */
    const nextSongImage = async () => {
        const nextSongEntry = getNextSong()
        if (nextSongEntry) {
            const { nextSong, index } = nextSongEntry
            const { entryMedia } = nextSong
            try {
                let imageMedia = getMediaDisplay(entryMedia)
                if (!imageMedia) {
                    imageMedia = getMediaThumbnail(entryMedia)
                }
                if (imageMedia) {
                    const { source } = imageMedia
                    const data = await downloadImageFile(source)
                    const src = URL.createObjectURL(data)
                    nextImageSrcRef.current = src
                    nextImageRef.current = source
                    nextImageIndex.current = index
                }
            } catch (error) {
                //displayError(error, setError, 51)
            }
        } else {
            nextImageSrcRef.current = null
            nextImageRef.current = null
            nextImageIndex.current = -1
        }
    }

    /**
     * Looping is managed in MediaPlayer.
     * @returns 
     */
    const getHtmlComponent = () => {
        //log(`<>MediaHtmlComponent getHtmlComponent  muted ${muted} imageSrc`, imageSrc)
        if (muted) {
            return (
                <div style={getVideoContainerStyle()}>
                    <video
                        ref={controlRef}
                        playsInline
                        muted
                        controls={controls}
                        loop={loop}
                        crossOrigin='use-credentials'
                        src={videoSource}
                        style={getVideoStyle()}
                        onClick={() => click ? click() : {}}
                    />
                </div>
            )
        } else {

            return (
                <div style={getVideoContainerStyle()}>
                    <video
                        ref={controlRef}
                        playsInline
                        controls={controls}
                        loop={loop}
                        crossOrigin='use-credentials'
                        src={videoSource}
                        style={getVideoStyle()}
                        onClick={() => click ? click() : {}}
                    />
                </div>
            )

        }
    }

    /** This is how we get rid of spurious vertical scroll bars, per Google AI
     * answer to "react prevent scrollbars with height 100vh". */
    useEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                const newHeight = window.innerHeight
                setContainerHeight(`${newHeight}px`)
            }
        }
        // Set initial height
        handleResize()
        // Update height on resize
        window.addEventListener('resize', handleResize)
        // Clean up listener
        return () => window.removeEventListener('resize', handleResize)
    }, [])
    useEffect(() => {
        getImageMedia()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playList, playListIndex, containDisplay])

    useEffect(() => {
        const handleOrientationChange = () => {
            setIsPortrait(window.screen.orientation.type.startsWith('portrait'))
        }
        window.screen.orientation.addEventListener('change', handleOrientationChange)
        return () => {
            window.screen.orientation.removeEventListener('change', handleOrientationChange)
        }
    }, [])

    return (
        <div ref={containerRef}
            style={{ height: getHeight(), overflowY: 'hidden' }}
            onClick={(evt) => {
                evt.stopPropagation()
                dispatch(toggleControls())
            }}>{getHtmlComponent()}
        </div>)
})
export default memo(MediaHtmlComponent)